<template>
  <v-dialog class="recurringDialog rounded-xl" v-model="dialogStatuses.active" width="80vw" persistent>
    <v-card class="rounded-xl pa-6">
      <v-card-title class="text-h5 d-flex align-center pa-0 pb-5">
        <img width="40" contain src="@Assets/svgs/add_event.svg" alt="add_event icon" />
        <span class="mx-2" v-if="isFetching">Fetching Event ...</span>
        <span class="mx-2" v-else-if="selected.id"> Update Event </span>
        <span class="mx-2" v-else> Add a new event </span>
        <v-spacer></v-spacer>
        <div class="d-flex jsutify-space-between align-center">
          <v-btn class="mx-2" v-if="selected.id" rounded color="error" outlined @click="deleteEvent">
            Delete
          </v-btn>
          <v-btn class="mx-2" rounded color="primary" outlined @click="closeUpdateDialog">
            Cancel
          </v-btn>
          <v-btn class="px-12" rounded color="primary"
            :disabled="!valid || dialogStatuses.isLoading || !dialogStatuses.hasChanges" @click="updateConfirmed()"
            :loading="dialogStatuses.isLoading">
            <v-icon left>{{ selected.id ? 'mdi-check' : 'mdi-plus' }}</v-icon>
            {{ selected.id ? 'Save Changes' : 'Add Event' }}
          </v-btn>
        </div>
      </v-card-title>
      <v-sheet outlined class="rounded-xl px-4">
        <v-form ref="updateEvent" v-model="valid">
          <v-row>
            <v-col cols="6">
              <v-card-title>Event Details</v-card-title>
              <v-container class="overflowing">
                <v-row>
                  <v-col cols="12">
                    <v-text-field hide-details outlined dense v-model="selected.text" :rules="[formRules.required]"
                      label="Event Title"></v-text-field>
                  </v-col>

                  <v-col cols="12">
                    <v-textarea outlined dense clearable hide-details rows="6" row-height="25" no-resize
                      label="Description" placeholder="Enter Event Description" v-model="selected.description">
                    </v-textarea>
                  </v-col>

                  <v-col cols="12">
                    <v-select attach :items="EventTypes" label="Event Type" outlined dense hide-details clearable
                      item-value="id" item-text="name" v-model="selected.eventType">
                    </v-select>
                  </v-col>

                  <v-col cols="6">
                    <DatePicker key="start-date" :top="true" v-model="selected.start_date" :rules="[formRules.required]"
                      label="Start Date">
                    </DatePicker>
                  </v-col>

                  <v-col cols="6">
                    <TimePicker key="start-time" :top="true" v-model="selected.start_time" :rules="[formRules.required]"
                      label="Start Time"></TimePicker>
                  </v-col>

                  <v-col cols="6">
                    <DatePicker key="end-date" :top="true" v-model="selected.end_date" :rules="[formRules.required]"
                      label="End Date">
                    </DatePicker>
                  </v-col>

                  <v-col cols="6">
                    <TimePicker key="end-time" :top="true" v-model="selected.end_time" :rules="[formRules.required]"
                      label="End Time">
                    </TimePicker>
                  </v-col>

                  <v-col cols="12">
                    <v-select attach :items="RecurrenceType" label="Recurring" outlined dense hide-details
                      item-value="id" item-text="name" v-model="selected.frequency">
                    </v-select>
                  </v-col>

                  <v-col cols="6" v-if="selected.frequency">
                    <DatePicker key="recurring-date" :top="true" v-model="selected.recurring_end_date"
                      :rules="selected.frequency !== 0 ? [formRules.required] : []" label="End Date">
                    </DatePicker>
                  </v-col>

                  <v-col cols="6" v-if="selected.frequency">
                    <TimePicker key="recurring-time" :top="true" v-model="selected.recurring_end_time"
                      :rules="selected.frequency !== 0 ? [formRules.required] : []" label="End Time"></TimePicker>
                  </v-col>

                  <v-col cols="12">
                    <v-text-field hide-details outlined dense v-model="selected.location"
                      label="Location"></v-text-field>
                  </v-col>

                  <v-col cols="12">
                    <v-checkbox hide-details class="mt-0 mb-2" :ripple="false" v-model="selected.isOnline"
                      label="Online Meeting"></v-checkbox>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="6">
              <v-card-title class="d-flex justify-space-between align-center">
                <span>Schedule Reminder</span>
                <v-btn color="primary" outlined rounded v-if="!setReminder" @click="setReminder = true">
                  <v-icon left>mdi-plus</v-icon> Add Reminder
                </v-btn>
                <v-btn icon color="error" rounded v-if="setReminder" @click="clearReminder">
                  <v-icon>mdi-trash-can-outline</v-icon>
                </v-btn>
              </v-card-title>
              <v-container class="overflowing">
                <v-row>
                  <v-slide-y-transition>
                    <v-col cols="6" v-if="setReminder">
                      <DatePicker key="reminder-date" :top="true" v-model="selected.reminder_date"
                        :rules="[formRules.required]" label="Date">
                      </DatePicker>
                    </v-col>
                  </v-slide-y-transition>
                  <v-slide-y-transition>
                    <v-col cols="6" v-if="setReminder">
                      <TimePicker key="reminder-time" :top="true" v-model="selected.reminder_time"
                        :rules="[formRules.required]" label="Time"></TimePicker>
                    </v-col>
                  </v-slide-y-transition>

                  <v-col cols="12">
                    <v-autocomplete v-model="selected.participantIds" :items="users" dense outlined hide-details chips
                      label="Participants" item-text="firstName" item-value="id" multiple>
                      <template v-slot:selection="data">
                        <v-chip class="my-2" v-bind="data.attrs" :input-value="data.selected" close text-color="primary"
                          color="light_Primary3" @click="data.select" @click:close="removeUser(data.item)">
                          <v-avatar left>
                            <v-img :src="data.item.avatarUrl"></v-img>
                          </v-avatar>
                          {{ data.item.firstName + ' ' + data.item.lastName }}
                        </v-chip>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12">
                    <v-autocomplete v-model="selected.taskIds" :items="tasks" dense hide-details outlined chips
                      label="Related Tasks" item-text="text" item-value="id" multiple>
                      <template v-slot:selection="data">
                        <v-chip class="my-2" v-bind="data.attrs" :input-value="data.selected" close text-color="primary"
                          color="light_Primary3" @click="data.select" @click:close="removeTask(data.item)">
                          <img width="20" class="me-2" contain src="@Assets/svgs/Task.svg" alt="task icon" />
                          {{ data.item.text }}
                        </v-chip>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12">
                    <v-card-title class="d-flex justify-space-between align-center px-0 pt-0">
                      <span>Attach Document</span>
                      <v-btn icon color="error" rounded @click="clear" v-if="uploading || eventDocuments.length > 0">
                        <v-icon>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </v-card-title>
                    <FileDropZone class="pe-2" ref="fileUploader" :uploadedFiles="eventDocuments"
                      @files-uploaded="handleFilesUploaded" @uploading="handleUploading"></FileDropZone>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>
        </v-form>
      </v-sheet>
    </v-card>
  </v-dialog>
</template>
<script>
import formRules from '@Share/config/form-rules';
import DatePicker from '@Share/components/VDatePicker';
import TimePicker from '@Share/components/VTimePicker';
import FileDropZone from '@Share/components/FileDropZone';
import { mapGetters } from 'vuex';
import { $handleError, $isEqual, } from '@/utils';

import { EventTypes, FrequencyType, RecurrenceType } from '@Share/config/enums';
import moment from 'dayjs';

export default {
  name: 'recurring-event',
  data() {
    return {
      EventTypes,
      FrequencyType,
      RecurrenceType,
      formRules,
      isFetching: false,
      selected: {
      },
      selectedCemented: {},
      valid: false,
      uploading: false,
      setReminder: false,
      dialogStatuses: {
        active: false,
        isLoading: false,
        isClosing: false,
        hasChanges: false,
      },
      eventId: null,
      eventDocuments: [],
    };
  },
  props: {},
  components: {
    DatePicker,
    TimePicker,
    FileDropZone,
  },
  computed: {
    ...mapGetters('USER', { users: 'GET_TYPEHEAD_ENTRIES' }),
    ...mapGetters('TASK', { tasks: 'GET_TASKS' }),
    taskTypeId() {
      return this.$route.params?.taskTypeId || this.$route.query?.taskTypeId;
    },
    isCreating() {
      return !this.selected.id;
    },
  },
  mounted() { },
  created() {
    this.$store.dispatch('USER/GET_TYPEHEAD');
  },
  methods: {
    openDialog(id, startDate, endDate) {
      let _startDate = moment(new Date()).format('YYYY-MM-DD');
      let _endDate = moment(new Date()).format('YYYY-MM-DD');
      let _startTime = '00:00';
      let _endTime = '00:00';

      if (startDate) {
        _startDate = moment(startDate).format('YYYY-MM-DD');
        _startTime = moment(startDate).format('HH:mm');
      }

      if (endDate) {
        _endDate = moment(endDate).format('YYYY-MM-DD');
        _endTime = moment(endDate).format('HH:mm');
      } else if (startDate) {
        _endDate = moment(startDate).format('YYYY-MM-DD');
        _endTime = moment(startDate).add(1, 'hour').format('HH:mm');
      }

      if (id != null) {
        this.eventId = id;
        this.fetchEvent();
        setTimeout(() => {
          this.$refs.updateEvent.resetValidation();
        });
      }
      else {
        this.selected = {
          start_date: _startDate ?? moment(new Date()).format('YYYY-MM-DD'),
          end_date: _endDate ?? moment(new Date()).format('YYYY-MM-DD'),
          start_time: _startTime ?? '00:00',
          end_time: _endTime ?? '00:00',
        }

      }
      this.dialogStatuses.active = true;
    },
    deleteEvent() {
      this.$dialog.error({
        text: 'Are you sure you want to delete selected event?',
        title: 'Delete Event',
        actions: {
          false: 'Cancel',
          true: {
            text: 'Ok',
            color: 'primary',
            handle: () => {
              this.$store
                .dispatch('SCHEDULER/DELETE', {
                  id: this.eventId,
                  taskTypeId: this.taskTypeId,
                })
                .then(() => {
                  this.dialogStatuses.active = false;
                });
            },
          },
        },
      });
    },
    removeUser(selectedUser) {
      const index = this.selected.participantIds.map((e) => e).indexOf(selectedUser.id);
      if (index >= 0) this.selected.participantIds.splice(index, 1);
    },
    removeTask(selectedTask) {
      const index = this.selected.taskIds.map((e) => e).indexOf(selectedTask.id);
      if (index >= 0) this.selected.taskIds.splice(index, 1);
    },
    handleFilesUploaded(files) {
      this.selected.documents = files;
    },
    clear() {
      this.eventDocuments = [];
      this.$refs.fileUploader.clearAllFiles();
      this.uploading = false;
    },
    handleUploading(event) {
      this.uploading = event;
    },
    clearReminder() {
      this.setReminder = false;
      this.selected.reminder_date = moment(Date.now()).format('YYYY-MM-DD');
      this.selected.reminder_time = '00:00';
    },
    cementEvent() {
      this.selectedCemented = structuredClone(this.selected);
      this.checkForChanges();
    },
    checkForChanges() {
      if (!this.dialogStatuses.active) return;
      this.dialogStatuses.hasChanges = !$isEqual(this.selected, this.selectedCemented);
    },
    closeUpdateDialog() {
      this.selected = {};
      this.isFetching = false;
      this.cementEvent();
      this.dialogStatuses.active = false;
      this.valid = true;
      this.$refs.updateEvent.resetValidation();
    },
    convertToISOFormat(date, time) {
      const dateTimeString = `${date}T${time}:00.000Z`;
      const dateTime = new Date(dateTimeString);
      return dateTime.toISOString();
    },
    fetchEvent() {
      this.isFetching = true;
      this.$store
        .dispatch('SCHEDULER/GET_BY_ID', this.eventId)
        .then((resp) => {
          this.isFetching = false;
          this.dialogStatuses.isLoading = false;
          let data = structuredClone(resp.data);
          this.selected = data;

          // Update start date/time
          if (resp.data.start_date) {
            let startDateTime = moment(resp.data.start_date);
            this.selected.start_date = startDateTime.format('YYYY-MM-DD');
            this.selected.start_time = startDateTime.format('HH:mm');
          }

          // Update end date/time
          if (resp.data.endDate) {
            let endDateTime = moment(resp.data.endDate);
            this.selected.end_date = endDateTime.format('YYYY-MM-DD');
            this.selected.end_time = endDateTime.format('HH:mm');
          }

          // Update recurring end date/time
          if (resp.data.end_date) {
            let recurringEndDateTime = moment(resp.data.end_date);
            this.selected.recurring_end_date = recurringEndDateTime.format('YYYY-MM-DD');
            this.selected.recurring_end_time = recurringEndDateTime.format('HH:mm');
          }

          // Update reminder date/time
          if (this.selected.reminderDate) {
            let reminderDateTime = moment(this.selected.reminderDate);
            this.setReminder = true;
            this.selected.reminder_date = reminderDateTime.format('YYYY-MM-DD');
            this.selected.reminder_time = reminderDateTime.format('HH:mm');
          }

          this.selected.participantIds = this.selected.participants ? this.selected.participants.map((el) => el.id) : [];
          this.selected.taskIds = this.selected.tasks ? this.selected.tasks.map((el) => el.id) : [];

          delete this.selected.participants;
          delete this.selected.tasks;
          delete this.selected.reminderDate;

          this.eventDocuments = this.selected.documents || [];

          this.selected = { ...this.selected };

          this.cementEvent();

          // Force UI update
          this.$nextTick(() => {
            this.checkForChanges();
          });
        })
        .catch((err) => {
          this.isFetching = false;
          this.closeUpdateDialog();
          $handleError(err);
        });
    },
    updateConfirmed() {
      this.dialogStatuses.isLoading = true;
      let requestBody = structuredClone(this.selected);

      const dateFields = [
        { date: 'start_date', time: 'start_time', target: 'start_date' },
        { date: 'end_date', time: 'end_time', target: 'endDate' },
        { date: 'recurring_end_date', time: 'recurring_end_time', target: 'end_date' },
        { date: 'reminder_date', time: 'reminder_time', target: 'reminderDate' },
      ];

      dateFields.forEach((field) => {
        if (requestBody[field.date] && requestBody[field.time]) {
          requestBody[field.target] = this.convertToISOFormat(
            requestBody[field.date],
            requestBody[field.time],
          );
        }
      });

      if (!requestBody.frequency) {
        delete requestBody.end_date;
      }

      requestBody.taskTypeId = this.taskTypeId;
      requestBody.isRecurring = requestBody.frequency !== 0;

      this.$store
        .dispatch('SCHEDULER/UPDATE', requestBody)
        .then(() => {
          this.closeUpdateDialog();
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          this.dialogStatuses.isLoading = false;
        });
    },
    validateDateAndTime() {
      if (!this.selected.start_date || !this.selected.end_date) return;

      let startDate = moment(this.selected.start_date);
      let endDate = moment(this.selected.end_date);
      let recurringEndDate = moment(this.selected.recurring_end_date);

      if (endDate.isBefore(startDate)) {
        this.selected.end_date = startDate.format('YYYY-MM-DD');
      }
      if (recurringEndDate.isBefore(startDate)) {
        this.selected.recurring_end_date = startDate.format('YYYY-MM-DD');
      }

      if (!this.selected.end_time || !this.selected.start_time) return;

      let startTime = moment(`${this.selected.start_date} ${this.selected.start_time}`, 'YYYY-MM-DD HH:mm');
      let endTime = moment(`${this.selected.end_date} ${this.selected.end_time}`, 'YYYY-MM-DD HH:mm');

      if (endTime.isBefore(startTime)) {
        this.selected.end_time = this.selected.start_time;
      }
    }
  },
  watch: {
    selected: {
      handler() {
        this.validateDateAndTime();
        this.$nextTick(() => {
          this.checkForChanges();
        });
      },
      deep: true,
    },
  },
};
</script>
<style lang="scss">
.recurringDialog:not(.v-dialog--fullscreen) {
  max-height: 100%;
}

.v-dialog {
  border-radius: 25px;
  overflow-y: visible;
}

.overflowing {
  height: 70vh;
  overflow-y: auto;
}
</style>
